<script setup lang="ts">
const { t } = useI18n();

useHead({
  titleTemplate: () => t("mainNav.company.privacy") || 'Privacy Policy',
  meta: [
    { name: "description", content: "Privacy Policy and Data Handling practices for the SMSBAT Omnichannel Platform and Mobile App." },
  ]
});

onMounted(() => {
  document.querySelector("body")?.scrollIntoView();
});
</script>

<template>
  <div class="privacy-policy-page pt-[120px] pb-20 bg-gray-50 min-h-screen">
    <div class="container mx-auto max-w-4xl px-4">
      <div class="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-gray-100">
        
        <div class="text-center mb-12 border-b pb-8">
          <h1 class="text-4xl font-extrabold text-[#3070b6] mb-4">Privacy Policy</h1>
          <p class="text-gray-500">Effective Date: March 14, 2026</p>
        </div>

        <div class="prose prose-lg max-w-none text-gray-700">
          <p>
            We at SMSBAT, LLC. and its affiliates ("<strong>SMSBAT</strong>", "<strong>we</strong>", "<strong>us</strong>", "<strong>our</strong>") respect your privacy. This Privacy Policy explains our practices regarding the collection, use, and disclosure of your information when you use our web platform, Bots Constructor, Omnichannel APIs, and the SMSBAT Mobile App (collectively, the "<strong>Service</strong>").
          </p>

          <h2 class="text-2xl font-bold text-gray-900 mt-10 mb-4 border-l-4 border-[#3070b6] pl-4">1. Information We Collect</h2>
          
          <h3 class="text-xl font-semibold text-gray-800 mt-6 mb-3">1.1 Information You Provide to Us</h3>
          <p>
            When you register for an account, subscribe to our Service, or contact our support, we collect personally identifiable information such as your name, company name, email address, phone number, and billing information. When using our Bots Constructor, you may also provide configuration data, scripts, and flow logic.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mt-6 mb-3">1.2 Mobile App Specific Data (Google Play & App Store Compliance)</h3>
          <p>
            If you use the <strong>SMSBAT Omnichannel Mobile App</strong>, we may request access to the following device data to provide core chat functionality:
          </p>
          <ul class="list-disc pl-6 space-y-2">
            <li><strong>Contacts List (Read):</strong> To allow you to sync your device contacts with the SMSBAT CRM and initiate chats easily. We securely transmit this data via HTTPS. We do not sell or share your contact list with third parties.</li>
            <li><strong>Camera and Photo Library (Media):</strong> To enable you to capture photos/videos or attach media from your gallery directly into customer support chats. We only access media you explicitly select.</li>
            <li><strong>Microphone:</strong> To allow you to record and send voice messages within chats.</li>
            <li><strong>Push Notifications:</strong> To alert you of incoming messages from customers across Viber, Telegram, or Meta channels. You can manage these permissions in your OS settings.</li>
          </ul>

          <h3 class="text-xl font-semibold text-gray-800 mt-6 mb-3">1.3 Automatically Collected Information</h3>
          <p>
            When navigating our website or using our API, we collect standard log data including your IP address, browser type, operating system, and session durations (e.g., via Google Analytics) to ensure platform stability and security.
          </p>

          <h2 class="text-2xl font-bold text-gray-900 mt-10 mb-4 border-l-4 border-[#3070b6] pl-4">2. SMSBAT as a Data Processor</h2>
          <p>
            For the provisions of our conversational and omnichannel services, SMSBAT acts as a <strong>Data Processor</strong> on behalf of our clients (the Data Controllers). If you are an end-user chatting with a bot or live agent powered by SMSBAT (via Rakuten Viber, Meta, Telegram, or Webchat), we process your messages, phone number, and metadata strictly under the instructions of our client. 
          </p>

          <h2 class="text-2xl font-bold text-gray-900 mt-10 mb-4 border-l-4 border-[#3070b6] pl-4">3. How We Use the Information</h2>
          <p>We use the collected information for the following purposes:</p>
          <ul class="list-disc pl-6 space-y-2">
            <li>To provide, operate, and maintain the SMSBAT Platform and Mobile App.</li>
            <li>To process omnichannel message routing between integrated platforms (e.g., Rakuten Viber, Telegram, Meta).</li>
            <li>To process billing transactions and provide customer support.</li>
            <li>To enforce our Terms of Service and prevent fraudulent activities or unauthorized access (e.g., abuse of the API).</li>
          </ul>

          <h2 class="text-2xl font-bold text-gray-900 mt-10 mb-4 border-l-4 border-[#3070b6] pl-4">4. Sharing Information with Third Parties</h2>
          <p>
            We do not sell your personal data. We may share information with:
          </p>
          <ul class="list-disc pl-6 space-y-2">
            <li><strong>Service Providers:</strong> Cloud hosting (e.g., AWS, Cloudflare), payment processors, and analytics providers under strict confidentiality agreements.</li>
            <li><strong>Integrated Platforms:</strong> By using our integrations, messaging data must naturally traverse the networks of our partners (e.g., Rakuten Viber, Meta) subject to their respective privacy policies.</li>
            <li><strong>Legal Compliance:</strong> When required by law, subpoena, or to protect the safety and rights of SMSBAT, our users, or the public.</li>
          </ul>

          <h2 class="text-2xl font-bold text-gray-900 mt-10 mb-4 border-l-4 border-[#3070b6] pl-4">5. Data Retention & Security</h2>
          <p>
            We implement industry-standard encryption (TLS/SSL) for data in transit and encryption for data at rest. We retain account data as long as your account is active. Message history processed on behalf of our clients is retained according to the configuration and retention policies set by the client within their workspace.
          </p>

          <h2 class="text-2xl font-bold text-gray-900 mt-10 mb-4 border-l-4 border-[#3070b6] pl-4">6. Your Rights (GDPR & CCPA)</h2>
          <p>
            Depending on your jurisdiction, you may have the right to request access to, correction of, or deletion of your personal data. You may also have the right to restrict processing or request data portability. To exercise these rights as an SMSBAT account holder, please contact us. If you are an end-user of one of our clients, please direct your request to the respective client.
          </p>

          <h2 class="text-2xl font-bold text-gray-900 mt-10 mb-4 border-l-4 border-[#3070b6] pl-4">7. International Data Transfers</h2>
          <p>
            We may store and process data in the European Union, the United States, or other operational regions. We ensure that our infrastructure providers offer adequate data protection mechanisms, such as Standard Contractual Clauses (SCCs).
          </p>

          <h2 class="text-2xl font-bold text-gray-900 mt-10 mb-4 border-l-4 border-[#3070b6] pl-4">8. Changes to this Policy</h2>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our app features or regulatory requirements (e.g., App Store policy updates). We will notify you of any material changes by posting the new policy on this page with an updated "Effective Date."
          </p>

          <div class="mt-12 bg-indigo-50 p-6 rounded-xl border border-indigo-100">
            <h2 class="text-xl font-bold text-[#3070b6] mb-2">9. Contact Us</h2>
            <p class="mb-0">
              If you have any questions or complaints regarding this Privacy Policy or our mobile app data handling practices, please contact our Data Protection Officer at:
              <br>
              <a href="mailto:privacy@smsbat.com" class="text-indigo-600 font-semibold hover:underline mt-2 inline-block">privacy@smsbat.com</a>
            </p>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.prose {
  p {
    @apply mb-4 leading-relaxed;
  }
}
</style>
